import React, { useCallback, useRef } from 'react';

import { InputRadio, type InputRadioProps } from '../InputRadio';

import { Item, List } from './styles';

export interface InputRadioGroupItem
    extends Omit<InputRadioProps, 'groupName' | 'checked' | 'loading' | 'padded' | 'onChange'> {
    onChange?: InputRadioProps['onChange'];
}

export interface InputRadioGroupProps {
    items: InputRadioGroupItem[];
    groupName: InputRadioProps['groupName'];
    selectedValue?: InputRadioProps['value'];
    disableAll?: InputRadioProps['disabled'];
    awaitingChange?: boolean;
    onChangeAny: InputRadioProps['onChange'];
}

export function InputRadioGroup({
    items,
    groupName,
    selectedValue,
    disableAll = false,
    awaitingChange = false,
    onChangeAny,
}: InputRadioGroupProps) {
    const lastChangedId = useRef<InputRadioGroupItem['id']>();

    const handleChangeAny: InputRadioGroupProps['onChangeAny'] = useCallback(
        (result) => {
            lastChangedId.current = result.id;
            onChangeAny(result);
        },
        [onChangeAny],
    );

    const itemsMarkup = items.map(({ disabled, onChange, ...item }) => {
        const handleChange: InputRadioGroupProps['onChangeAny'] = (result) => {
            handleChangeAny(result);
            onChange?.(result);
        };

        return (
            <Item key={item.id}>
                <InputRadio
                    {...item}
                    padded
                    groupName={groupName}
                    checked={item.value === selectedValue}
                    disabled={disableAll || disabled || awaitingChange}
                    loading={awaitingChange && lastChangedId.current === item.id}
                    onChange={handleChange}
                />
            </Item>
        );
    });

    return itemsMarkup.length ? <List>{itemsMarkup}</List> : null;
}
